import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlineMenuAlt2,
  HiOutlineSearch,
  HiOutlineShoppingBag,
  HiOutlineX,
} from "react-icons/hi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import BrandMark from "./BrandMark";
import "../css/header.scss";

const fallbackShopLinks = [
  { label: "NEW ARRIVALS", category: "New Arrivals" },
  { label: "JACKETS", category: "Jackets" },
  { label: "SWEATERS", category: "Sweaters" },
  { label: "SHIRTS", category: "Shirts" },
  { label: "HEAVY DUTY COTTON", category: "Heavy Duty Cotton" },
  { label: "ACCESSORIES", category: "Accessories" },
  { label: "ALL PRODUCTS", category: "All" },
];
const fallbackAboutLinks = [
  { label: "OUR STORY", to: "/about#history" },
  { label: "OUR APPROACH", to: "/about#factory" },
  { label: "OUR COMPANY", to: "/about#retail-store" },
  { label: "CONTACT", to: "/contact" },
];

function Header() {
  const { cartItems } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const mobileMoreRef = useRef(null);
  const moreTriggerRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const searchPanelRef = useRef(null);

  const shopLinks = siteConfig.navigation?.shop || fallbackShopLinks;
  const aboutLinks = siteConfig.navigation?.about || fallbackAboutLinks;
  const moreLinks = siteConfig.navigation.more;
  const cartCount = cartItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );

  const searchResults = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return [];
    return productData
      .filter((product) =>
        [product.name, product.category, ...(product.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 5);
  }, [searchTerm]);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSearchTerm("");
    setMoreOpen(false);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const staticTitles = {
      "/about": "About",
      "/shipping": "Shipping",
      "/returns": "Returns & Exchanges",
      "/contact": "Contact",
      "/privacy": "Privacy Policy",
      "/terms": "Terms of Use",
      "/cookies": "Cookie Policy",
      "/faqs": "FAQs",
      "/cartpage": "Shopping Cart",
      "/checkout": "Checkout",
      "/paysuccess": "Order Status",
    };
    const pageTitle = staticTitles[location.pathname];
    if (pageTitle) document.title = `${pageTitle} | ${siteConfig.brandName}`;
  }, [location.pathname]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        if (!moreOpen) setMenuOpen(false);
        setSearchOpen(false);
        setMoreOpen(false);
        if (moreRef.current?.contains(document.activeElement) || mobileMoreRef.current?.contains(document.activeElement)) {
          moreTriggerRef.current?.focus();
        }
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const closeOutside = (event) => {
      if (!moreRef.current?.contains(event.target) && !mobileMoreRef.current?.contains(event.target)) setMoreOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("focusin", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("focusin", closeOutside);
    };
  }, [moreOpen]);

  const toggleMore = (event) => {
    moreTriggerRef.current = event.currentTarget;
    setMoreOpen((open) => !open);
  };

  useEffect(() => {
    if (!searchOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const searchTrigger = searchTriggerRef.current;
    document.body.style.overflow = "hidden";

    const trapFocus = (event) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        searchPanelRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled])'
        ) || []
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      document.body.style.overflow = previousOverflow;
      searchTrigger?.focus();
    };
  }, [searchOpen]);

  const categoryHref = (category) => {
    if (!category || category === "All" || category === "All Products") {
      return "/shop";
    }
    return `/shop?category=${encodeURIComponent(category)}`;
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    if (query) navigate(`/shop?search=${encodeURIComponent(query)}`);
  };

  return (
    <header className="site-header">
      <div className="site-header__utility">
        <span>{siteConfig.tagline}</span>
        <span>{siteConfig.origin}</span>
      </div>

      <div className="site-header__bar">
        <button
          className="site-header__mobile-trigger"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => {
            setMenuOpen((open) => !open);
            setMoreOpen(false);
          }}
        >
          {menuOpen ? <HiOutlineX /> : <HiOutlineMenuAlt2 />}
          <span>MENU</span>
        </button>

        <nav className="site-header__desktop-nav" aria-label="Primary navigation">
          <div className="nav-item nav-item--has-panel">
            <Link to="/shop">SHOP</Link>
            <div className="nav-panel">
              <div className="nav-panel__intro">
                <p>SHOP THE COLLECTION</p>
                <strong>Find a little everyday comfort.</strong>
              </div>
              <div className="nav-panel__links">
                {shopLinks.map((item) => (
                  <Link key={item.label} to={categoryHref(item.category)}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="nav-item nav-item--has-panel nav-item--about">
            <Link to="/about">ABOUT</Link>
            <div className="nav-panel nav-panel--compact">
              <div className="nav-panel__links">
                {aboutLinks.map((item) => (
                  <Link key={item.label} to={item.to}>{item.label}</Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/contact">CONTACT</Link>
          <div className="nav-more" ref={moreRef}>
            <button type="button" aria-expanded={moreOpen} aria-controls="more-navigation" onClick={toggleMore}>
              MORE <span aria-hidden="true">{moreOpen ? "−" : "+"}</span>
            </button>
            <div id="more-navigation" className="nav-more__panel" hidden={!moreOpen}>
              {moreLinks.map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
            </div>
          </div>
        </nav>

        <Link
          className="site-header__brand"
          to="/"
          aria-label={`${siteConfig.brandName} home`}
        >
          <BrandMark />
        </Link>

        <div className="site-header__actions">
          <span className="site-header__currency">
            {siteConfig.currency?.code || "USD"}
          </span>
          <button
            ref={searchTriggerRef}
            type="button"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <HiOutlineSearch />
          </button>
          <Link
            className="site-header__cart"
            to="/cartpage"
            aria-label={`Cart with ${cartCount} items`}
          >
            <HiOutlineShoppingBag />
            <span>CART ({cartCount})</span>
          </Link>
        </div>
      </div>

      <div
        id="mobile-navigation"
        className={`mobile-navigation ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <nav aria-label="Mobile navigation">
          <p>SHOP</p>
          {shopLinks.map((item) => (
            <Link key={item.label} to={categoryHref(item.category)}>
              {item.label}
            </Link>
          ))}
          <p>ABOUT</p>
          {aboutLinks.map((item) => (
            <Link key={item.label} to={item.to}>{item.label}</Link>
          ))}
          <div className="mobile-navigation__more" ref={mobileMoreRef}>
            <button type="button" aria-expanded={moreOpen} aria-controls="mobile-more-navigation" onClick={toggleMore}>
              MORE <span aria-hidden="true">{moreOpen ? "−" : "+"}</span>
            </button>
            <div id="mobile-more-navigation" hidden={!moreOpen}>
              {moreLinks.map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
            </div>
          </div>
        </nav>
      </div>

      {searchOpen && (
        <div
          className="search-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Product search"
        >
          <button
            className="search-drawer__backdrop"
            type="button"
            aria-label="Close search"
            onClick={() => setSearchOpen(false)}
          />
          <div className="search-drawer__panel" ref={searchPanelRef}>
            <div className="search-drawer__heading">
              <span>SEARCH</span>
              <button
                type="button"
                aria-label="Close search"
                onClick={() => setSearchOpen(false)}
              >
                <HiOutlineX />
              </button>
            </div>
            <form onSubmit={handleSearchSubmit}>
              <HiOutlineSearch aria-hidden="true" />
              <input
                autoFocus
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search jackets, sweaters, shirts..."
                aria-label="Search products"
              />
              <button type="submit">SEARCH</button>
            </form>
            {searchTerm && (
              <div className="search-drawer__results" aria-live="polite">
                {searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <Link key={product.id} to={`/shopDetails/${product.id}`}>
                      <img
                        src={product.image}
                        alt=""
                        width="72"
                        height="90"
                      />
                      <span>
                        <strong>{product.name}</strong>
                        <small>
                          {siteConfig.currency?.symbol || "$"}
                          {Number(product.price).toFixed(2)}
                        </small>
                      </span>
                    </Link>
                  ))
                ) : (
                  <p>No products found. Try a broader search.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
